/**
 * API base URL resolver.
 *
 * - On `majourneys.com` the browser calls `api.majourneys.com` directly
 *   (CORS is whitelisted for that origin).
 * - Every other consumer — city sites (visit-fes, etc.) and attraction
 *   microsites (hassan-mosque, etc.) — runs a `/api/*` proxy inside its
 *   Cloudflare Worker, so the browser stays same-origin and avoids the
 *   preflight altogether. Detect via current origin.
 * - In SSR (no `window`) every consumer reads `VITE_API_URL` and hits
 *   the upstream directly (server-to-server, CORS doesn't apply).
 *
 * The no-env fallback differs by build mode so a `vite build` that runs
 * without VITE_API_URL doesn't silently bake `http://localhost:5050` into
 * the production bundle — that exact miss shipped a Worker where the
 * booking thank-you page (and cancel / modify / resend-email) fetched
 * localhost and 500'd for every customer on majourneys.com. Mirrors
 * apps/majourneys/src/lib/api-base.ts.
 */
export function apiBase(): string {
  if (typeof window !== "undefined") {
    const { protocol, hostname, origin } = window.location;
    // Skip the proxy logic in dev (localhost / 127.0.0.1 / *.local) and on
    // majourneys.com itself — both reach the upstream directly. Every other
    // https origin (city + attraction microsites) goes through /api.
    const isLocal =
      hostname === "localhost" ||
      hostname === "127.0.0.1" ||
      hostname.endsWith(".local");
    const isMajourneysApex =
      origin === "https://majourneys.com" || origin === "https://www.majourneys.com";
    if (protocol === "https:" && !isLocal && !isMajourneysApex) {
      return `${origin}/api`;
    }
  }
  const explicit = import.meta.env.VITE_API_URL;
  if (explicit) return explicit;
  return import.meta.env.DEV ? "http://localhost:5050" : "https://api.majourneys.com";
}

/** Standard headers for raw fetches against the API. */
export function apiHeaders(extra?: HeadersInit): Headers {
  return new Headers(extra);
}
